import { ref, computed } from 'vue';

/**
 * L'OUTIL S'INSTALLE — état et gestes de l'installation en application.
 *
 * ===========================================================================
 * CE QU'ON GAGNE : une icône, une fenêtre à soi (sans barre d'adresse) — et,
 * sur iPhone, la CONDITION pour recevoir des notifications système : iOS ne
 * délivre de push qu'à une application ajoutée à l'écran d'accueil. C'est la
 * raison pour laquelle cette brique précède celle des notifications.
 *
 * CE QU'ON NE GAGNE PAS : le hors-ligne. Cf. l'en-tête de `public/sw.js`.
 *
 * TROIS SITUATIONS, ET UNE SEULE PORTE. Le navigateur ne se comporte pas
 * pareil selon la plateforme :
 *
 *   - Chrome / Edge (Windows, Android) émettent `beforeinstallprompt`. On le
 *     RETIENT au lieu de le laisser filer : le navigateur choisirait son
 *     moment, en général à la deuxième visite, au milieu d'une saisie.
 *   - Safari (iPhone, iPad) n'émet rien et n'offre aucune API : l'installation
 *     est un geste manuel de l'utilisateur. On ne peut que la lui décrire.
 *   - Firefox de bureau n'installe pas du tout. On n'affiche alors rien —
 *     proposer une action qui n'aboutira pas est pire que se taire.
 * ===========================================================================
 */

/** L'événement de Chrome, absent des types du DOM (proposition non normalisée). */
interface InvitationInstallation extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * Refus mémorisé PAR APPAREIL, et non par compte.
 *
 * Installer est une propriété de la machine qu'on a devant soi : quelqu'un qui
 * a dit non sur son poste fixe doit pouvoir se le voir proposer sur son
 * téléphone, et l'inverse. Une clé par compte aurait reposé la question sur le
 * poste où l'on venait de la refuser.
 */
const CLE_REFUS = 'pa.pwa.refuse';

const invitation = ref<InvitationInstallation | null>(null);
const installee = ref(false);
const refusee = ref(lire());
const enCours = ref(false);

function lire(): boolean {
  try {
    return localStorage.getItem(CLE_REFUS) === '1';
  } catch {
    // Navigation privée, stockage bloqué : on repose la question, ce n'est pas grave.
    return false;
  }
}

/** Déjà lancée en fenêtre à elle : inutile de proposer quoi que ce soit. */
function detecterInstallee(): boolean {
  if (typeof matchMedia === 'undefined') return false;
  return (
    matchMedia('(display-mode: standalone)').matches ||
    matchMedia('(display-mode: window-controls-overlay)').matches ||
    // Safari iOS, qui n'implémente pas `display-mode`.
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/**
 * iPhone / iPad, où l'installation se fait à la main.
 *
 * iPadOS se présente comme un Mac depuis la version 13 : c'est le nombre de
 * points de contact qui le trahit, un Mac n'en ayant aucun.
 */
function estIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

export type ModeInstallation = 'invitation' | 'manuel' | 'aucun';

/**
 * Comment l'installation peut se faire ici :
 *  - `invitation` : le navigateur nous a confié son invitation, un bouton suffit ;
 *  - `manuel` : iOS — on explique le geste ;
 *  - `aucun` : déjà installée, ou navigateur qui n'installe pas.
 */
export const modeInstallation = computed<ModeInstallation>(() => {
  if (installee.value) return 'aucun';
  if (invitation.value) return 'invitation';
  return estIOS() ? 'manuel' : 'aucun';
});

/** Vrai quand il y a quelque chose à proposer ET qu'on ne l'a pas déjà refusé. */
export const proposerInstallation = computed(
  () => modeInstallation.value !== 'aucun' && !refusee.value,
);

/** Vrai quand l'outil tourne déjà en application installée. */
export const estInstallee = computed(() => installee.value);
export const installationEnCours = computed(() => enCours.value);

/** « Plus tard » : on ne repose plus la question sur cet appareil. */
export function refuserInstallation(): void {
  refusee.value = true;
  try {
    localStorage.setItem(CLE_REFUS, '1');
  } catch {
    /* stockage indisponible : le refus ne vaut que pour la session */
  }
}

/**
 * Déclenche l'installation. Rend `true` si elle a été acceptée.
 *
 * L'invitation ne sert QU'UNE FOIS : acceptée ou non, le navigateur la
 * consomme. La rejouer lèverait une erreur, d'où le vidage inconditionnel.
 */
export async function installer(): Promise<boolean> {
  const evt = invitation.value;
  if (!evt) return false;
  enCours.value = true;
  try {
    await evt.prompt();
    const { outcome } = await evt.userChoice;
    if (outcome === 'accepted') installee.value = true;
    else refuserInstallation();
    return outcome === 'accepted';
  } catch {
    return false;
  } finally {
    invitation.value = null;
    enCours.value = false;
  }
}

/**
 * Enregistre le service worker et branche l'écoute des événements
 * d'installation. Appelé une fois, au démarrage.
 *
 * ON ENREGISTRE AUSSI EN DÉVELOPPEMENT (localhost est un contexte sûr aux yeux
 * des navigateurs). Un service worker qui ne s'activerait qu'en production
 * serait le seul morceau de l'application qu'on ne découvrirait qu'en recette
 * — c'est-à-dire la meilleure façon d'y livrer une panne.
 */
export function initInstallation(): void {
  if (typeof window === 'undefined') return;

  installee.value = detecterInstallee();

  window.addEventListener('beforeinstallprompt', (e) => {
    // Sans cela Chrome pose SA bannière, à SON moment.
    e.preventDefault();
    invitation.value = e as InvitationInstallation;
  });

  window.addEventListener('appinstalled', () => {
    installee.value = true;
    invitation.value = null;
  });

  if ('serviceWorker' in navigator) {
    // Après `load` : l'enregistrement d'un service worker entre en concurrence
    // avec le chargement des scripts de l'application, qui, lui, est visible.
    window.addEventListener('load', () => {
      void navigator.serviceWorker.register('/sw.js').catch(() => {
        // Contexte non sûr, ou service workers coupés par la stratégie de
        // groupe : l'application marche sans, elle n'est simplement pas
        // installable. Rien à dire à l'utilisateur.
      });
    });
  }
}
